import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fetchFusionAccess } from "@/lib/useFusionAccess";
import Logo from "@/components/Logo";
import BackgroundVideo from "@/components/BackgroundVideo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { setUser, fetchTeam } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      if (data?.team_id) await fetchTeam();
      let to = loc.state?.from?.pathname || (data.role === "admin" ? "/admin" : data.role === "coach" ? "/dashboard" : "/portal");
      if (!loc.state?.from?.pathname && (data.role === "coach" || data.role === "player")) {
        const access = await fetchFusionAccess();
        if (!access.biohacker_enabled) {
          to = data.role === "coach" ? "/dashboard/fusion" : "/portal/fusion";
        }
      }
      nav(to, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ backgroundColor: "var(--ink-1)" }}>
      {/* Left: video panel */}
      <div className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden">
        <BackgroundVideo />
        <Logo size="md" variant="lockup" linkTo="/" className="relative z-10" inverted dark />
        <div className="relative z-10 max-w-md">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
            Coach Console
          </div>
          <h2 className="font-display font-black text-5xl tracking-tighter mt-3 leading-[0.95]">
            Brand your tenant.<br />Track your arms.<br />Catch red flags early.
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed">
            Workload-aware hybrid tracks, baseline biomechanics, FlightScope &amp; Rapsodo ingestion, and instant CRITICAL SHUTDOWN alerts — all in one console.
          </p>
        </div>
        <div className="relative z-10 text-[11px] text-white/40 tracking-[0.25em] uppercase font-semibold">
          Healthy Arms · Healthy Bodies · Elite Performance
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12" style={{ backgroundColor: "var(--ink-1)" }}>
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="md:hidden mb-10"><Logo size="sm" variant="lockup" linkTo="/" dark /></div>
          <h1 className="font-display font-black text-4xl tracking-tighter text-white">Sign in</h1>
          <p className="text-white/50 mt-2 text-sm">
            New here? <Link to="/signup" className="font-semibold" style={{ color: "var(--brand-secondary)" }}>Create a coach account</Link>
          </p>

          <div className="space-y-4 mt-8">
            <div>
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@armiq.demo"
                className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-white/70">Password</Label>
                <Link to="/forgot-password" className="text-xs text-white/50 hover:text-white" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" data-testid="login-password-input" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="armiq123"
                className="mt-2 h-12 bg-[var(--ink-2)] border-[var(--ink-border)] text-white placeholder:text-white/30" />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-12 rounded-md text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}>
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="ml-1.5 h-4 w-4" /></>}
            </Button>
          </div>

          <div className="flex items-center gap-3 my-6 text-xs text-white/30 uppercase tracking-[0.25em]">
            <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
          </div>

          <Button type="button" variant="outline" onClick={loginGoogle} data-testid="login-google-button"
            className="w-full h-12 rounded-md border-2 border-white/15 bg-white/5 text-white hover:bg-white/10">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/><path fill="#FBBC04" d="M11.69 28.18A13.5 13.5 0 0 1 10.97 24c0-1.45.25-2.85.72-4.18v-5.7H4.34A21.98 21.98 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/></svg>
            Continue with Google
          </Button>
        </form>
      </div>
    </div>
  );
}
